import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Check, Minus } from 'lucide-react';

// Table Root
interface TableProps {
  children: ReactNode;
  className?: string;
}

export function Table({ children, className = '' }: TableProps) {
  return (
    <div className={`w-full overflow-x-auto ${className}`}>
      <table className="w-full border-collapse">
        {children}
      </table>
    </div>
  );
}

// Table Header
interface TableHeaderProps {
  children: ReactNode;
  className?: string;
}

export function TableHeader({ children, className = '' }: TableHeaderProps) {
  return (
    <thead className={`border-b border-border ${className}`}>
      {children}
    </thead>
  );
}

// Table Body
interface TableBodyProps {
  children: ReactNode;
  className?: string;
}

export function TableBody({ children, className = '' }: TableBodyProps) {
  return (
    <tbody className={`divide-y divide-border ${className}`}>
      {children}
    </tbody>
  );
}

// Table Row
interface TableRowProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  hoverable?: boolean;
}

export function TableRow({ children, className = '', onClick, hoverable = true }: TableRowProps) {
  return (
    <tr
      onClick={onClick}
      className={`
        ${hoverable ? 'hover:bg-background-hover/50 transition-colors' : ''}
        ${onClick ? 'cursor-pointer' : ''}
        ${className}
      `}
    >
      {children}
    </tr>
  );
}

// Table Head Cell
interface TableHeadProps {
  children: ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
}

export function TableHead({ children, className = '', align = 'left' }: TableHeadProps) {
  const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[align];

  return (
    <th
      className={`
        px-3 py-2.5 sm:px-4 sm:py-3 text-[11px] sm:text-[13px] font-medium text-text-muted whitespace-nowrap
        ${alignClass}
        ${className}
      `}
    >
      {children}
    </th>
  );
}

// Table Cell
interface TableCellProps {
  children?: ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
  colSpan?: number;
}

export function TableCell({ children, className = '', align = 'left', colSpan }: TableCellProps) {
  const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[align];

  return (
    <td
      colSpan={colSpan}
      className={`
        px-3 py-2.5 sm:px-4 sm:py-3 text-xs sm:text-sm text-text-primary whitespace-nowrap
        ${alignClass}
        ${className}
      `}
    >
      {children}
    </td>
  );
}

// Table Checkbox — compact checkbox for table rows
interface TableCheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
}

export function TableCheckbox({ checked, indeterminate = false, onChange }: TableCheckboxProps) {
  return (
    <motion.button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      whileTap={{ scale: 0.9 }}
      className={`
        w-[18px] h-[18px] rounded border-2 flex items-center justify-center flex-shrink-0
        transition-colors duration-200
        focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1
        ${checked || indeterminate
          ? 'bg-primary border-primary'
          : 'border-border bg-transparent hover:border-primary/50'
        }
      `}
    >
      {(checked || indeterminate) && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        >
          {indeterminate ? (
            <Minus className="w-3 h-3 text-white" strokeWidth={3} />
          ) : (
            <Check className="w-3 h-3 text-white" strokeWidth={3} />
          )}
        </motion.div>
      )}
    </motion.button>
  );
}

// Empty State for Table
interface TableEmptyProps {
  colSpan: number;
  message?: string;
  icon?: ReactNode;
}

export function TableEmpty({ colSpan, message = 'No data available', icon }: TableEmptyProps) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-12 text-center">
        <div className="flex flex-col items-center gap-2 text-text-secondary">
          {icon && <div className="text-text-muted">{icon}</div>}
          <span className="text-sm">{message}</span>
        </div>
      </td>
    </tr>
  );
}
